import { COLUMN_CATALOG_MAP } from './constants'
import { toNumber } from './calcResolve'

export const COMPARISON_OPERATIONS = {
  difference: { label: 'A minus B', compute: (a, b) => a - b },
  absoluteDifference: { label: '|A minus B|', compute: (a, b) => Math.abs(a - b) },
  ratio: { label: 'A / B', compute: (a, b) => b === 0 ? null : a / b },
  percentChange: { label: '% Change (A vs B)', compute: (a, b) => b === 0 ? null : (a - b) / b }
}

export const COMPARISON_OPERATION_OPTIONS = Object.entries(COMPARISON_OPERATIONS).map(
  ([value, { label }]) => ({ value, label })
)

/**
 * Finds a column instance by id with type fallback. Excludes comparison and
 * window columns to prevent circular references.
 */
function findInstance (id, columnType, columnInstances, catalogMap) {
  if (id) {
    const exact = columnInstances.find(c => c.id === id)
    if (exact && catalogMap[exact.columnType]?.kind !== 'comparison' && catalogMap[exact.columnType]?.kind !== 'window') {
      return exact
    }
  }
  if (!columnType) return null
  return columnInstances.find(
    c => c.columnType === columnType && catalogMap[c.columnType]?.kind !== 'comparison' && catalogMap[c.columnType]?.kind !== 'window'
  ) || null
}

/**
 * Mutates `row` to add values for comparison column instances.
 * Reads two same-row column values and applies the chosen arithmetic operation.
 */
export function applyComparisonColumns (row, columnInstances, catalogMap) {
  if (row?.rowType === 'benchmark') {
    for (const col of columnInstances) {
      if (catalogMap[col.columnType]?.kind === 'comparison') row[col.id] = null
    }
    return
  }
  for (const col of columnInstances) {
    const catalog = catalogMap[col.columnType]
    if (!catalog || catalog.kind !== 'comparison') continue
    const op = COMPARISON_OPERATIONS[col.options?.operation]
    if (!op) { row[col.id] = null; continue }
    const instA = findInstance(col.options?.columnAId, col.options?.columnAType, columnInstances, catalogMap)
    const instB = findInstance(col.options?.columnBId, col.options?.columnBType, columnInstances, catalogMap)
    if (!instA || !instB) { row[col.id] = null; continue }
    const a = toNumber(row[instA.id])
    const b = toNumber(row[instB.id])
    if (a == null || b == null) { row[col.id] = null; continue }
    row[col.id] = op.compute(a, b)
  }
}

/**
 * After `regenerateColumnIds`, comparison columns that stored columnAIndex / columnBIndex
 * need those indices resolved back to the new instance ids.
 */
export function resolveComparisonColumnRefs (columns, catalogMap) {
  return columns.map(col => {
    const catalog = catalogMap[col.columnType]
    if (!catalog || catalog.kind !== 'comparison') return col

    const opts = { ...col.options }
    let changed = false

    const resolveRef = (idxKey, idKey, typeKey) => {
      const idx = opts[idxKey]
      if (idx != null && idx >= 0 && idx < columns.length) {
        opts[idKey] = columns[idx].id
        opts[idxKey] = undefined
        changed = true
      } else if (opts[typeKey]) {
        const match = columns.find(
          c => c.columnType === opts[typeKey] &&
            catalogMap[c.columnType]?.kind !== 'comparison' &&
            catalogMap[c.columnType]?.kind !== 'window'
        )
        if (match) {
          opts[idKey] = match.id
          changed = true
        }
      }
    }

    resolveRef('columnAIndex', 'columnAId', 'columnAType')
    resolveRef('columnBIndex', 'columnBId', 'columnBType')

    return changed ? { ...col, options: opts } : col
  })
}

export function isComparisonColumnType (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'comparison'
}
